import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import { SiteChangeService } from '../service/siteChangeService';
import { SiteChangeOptions, SiteChangeSource } from '../types/SiteChangeTypes';
import { IApplicationState } from '../store';
import ISite from '../interfaces/ISite';

/**
 * Custom hook for site/venue changes
 * Provides a flexible React-friendly interface to the SiteChangeService
 * 
 * This hook provides two main methods:
 * - changeSite: For selecting a site with full control over options
 * - clearSite: For clearing the selected site
 * 
 * Callers can specify exactly which behaviors they want rather than being
 * locked into predefined presets.
 */
const useSiteChange = () => {
    const dispatch = useDispatch() as ThunkDispatch<IApplicationState, any, any>;
    const store = useStore<IApplicationState>();

    /**
     * Changes the selected site with flexible configuration options
     * @param site The site to change to
     * @param options Configuration options (uses sensible defaults if not specified)
     * @param nearest Whether this site was selected as the nearest location (default: false)
     * @param nearby Whether this site was selected from a nearby venue widget (default: false)
     */
    const changeSite = useCallback(
        async (
            site: ISite,
            options: Partial<SiteChangeOptions> = {},
            nearest: boolean = false,
            nearby: boolean = false,
            locationWidget: boolean = false
        ): Promise<void> => {
            // Provide sensible defaults while allowing full customization
            const defaultOptions: SiteChangeOptions = {
                source: SiteChangeSource.UserSelection,
                clearBooking: true,
                clearVoucher: true,
                loadOpenDates: true,
                trackAnalytics: true,
                handleNavigation: true,
                skipCodeClearing: false,
                skipModalDialogs: false,
            };

            const finalOptions = { ...defaultOptions, ...options };

            return SiteChangeService.changeSite(
                site,
                nearest,
                nearby,
                locationWidget,
                dispatch,
                store.getState,
                finalOptions
            );
        },
        [dispatch, store]
    );

    /**
     * Clears the selected site
     * @param options Configuration options for the clear operation
     */
    const clearSite = useCallback(
        async (options: Partial<SiteChangeOptions> = {}): Promise<void> => {
            const defaultOptions: SiteChangeOptions = {
                source: SiteChangeSource.UserSelection,
                clearBooking: true,
                clearVoucher: true,
                loadOpenDates: false,
                trackAnalytics: false,
                handleNavigation: true,
                skipCodeClearing: false,
                skipModalDialogs: true,
            };

            const finalOptions = { ...defaultOptions, ...options };
            return SiteChangeService.clearSite(dispatch, finalOptions, store.getState);
        },
        [dispatch, store]
    );

    return {
        changeSite,
        clearSite,
    };
};

export default useSiteChange;
